import type { CSSProperties, ReactElement, ReactNode } from 'react';
import {
  cloneElement,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled, { css } from 'styled-components';
import { useDelayedUnmount } from '@elfsight/lib-utils';
import { useShadowGlobalProps } from '@elfsight/widget-shadow';
import { createReadableClass } from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import { useRect } from 'react-use-rect';
import { Backdrop } from '../backdrop';
import { Portal } from '../portal';
import {
  consumeEvent,
  findRelativeParent,
  useOutsideClick,
  useScrollLock
} from '../utils';
import { zIndexLayer } from '../zIndexLayer';

const MIN_EDGE_DISTANCE = 4;
const ANIMATION_DURATION_MS = 300;
const CLOSING_CLASS = 'popout-content-closing';

export type Direction = 'upward' | 'downward';

export interface PopoutContentInnerProperties {
  effectiveDirection: Direction;
  anchorWidth: number;
  anchorHeight: number;
}

export interface PopoutProps {
  show: boolean;
  anchorElement: Element | null;
  children?:
    | ReactNode
    | ((innerProperties: PopoutContentInnerProperties) => ReactNode);
  direction?: Direction;
  alignment?: 'left' | 'center' | 'right';
  offsetTop?: number;
  offsetLeft?: number;
  disableScroll?: boolean;
  closeOnOutsideClick?: boolean;
  preventCloseOnAnchorElementClick?: boolean;
  withBackdrop?: boolean;
  backdropComponent?: ReactElement;
  backdropColor?: string;
  withoutTransition?: boolean;
  zIndex?: number;
  portalContainerElement?: Element;
  contentStyles?: CSSProperties;
  shadow?: boolean;
  onCloseRequest?(event?: MouseEvent | PointerEvent): void;
}

export const Popout = forwardRef<HTMLDivElement, PopoutProps>(
  (
    {
      show,
      children,
      anchorElement,
      direction = 'downward',
      alignment = 'left',
      offsetTop = 0,
      offsetLeft = 0,
      disableScroll = false,
      closeOnOutsideClick = false,
      preventCloseOnAnchorElementClick = false,
      withBackdrop = false,
      backdropComponent,
      backdropColor,
      withoutTransition = false,
      zIndex = zIndexLayer.modal,
      portalContainerElement,
      contentStyles,
      shadow,
      onCloseRequest
    },
    forwardingRef
  ) => {
    const { mode: isShadow } = useShadowGlobalProps() ?? {};
    const [anchorRect, setAnchorRect] = useState({
      width: 0,
      height: 0,
      top: 0,
      left: 0
    });
    const [anchorRectRef] = useRect(setAnchorRect, {
      resize: true
    });

    // start to measure the anchor element when Popout showed
    useEffect(() => {
      if (!show) {
        return;
      }

      anchorRectRef(anchorElement);
      return () => anchorRectRef(null);
    }, [show, anchorElement, anchorRectRef]);

    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);

    const [contentRect, setContainerRect] = useState({ width: 0, height: 0 });

    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const contentRectRef = useCallback((node: HTMLDivElement | null) => {
      resizeObserverRef.current?.disconnect();

      if (node) {
        setContainerRect(node.getBoundingClientRect());

        resizeObserverRef.current = new ResizeObserver(() => {
          setContainerRect(node.getBoundingClientRect());
        });
        resizeObserverRef.current.observe(node);
      }
    }, []);

    useEffect(() => {
      const target = anchorElement?.getRootNode();
      const scrollTarget = (() => {
        if (target instanceof Document) {
          return window;
        }

        if (target instanceof ShadowRoot) {
          return target.getRootNode();
        }

        return;
      })();
      if (!scrollTarget) {
        return;
      }

      const handleScroll = () => {
        if (contentRef.current) {
          setContainerRect(contentRef.current.getBoundingClientRect());
        }
      };

      scrollTarget.addEventListener('scroll', handleScroll, {
        passive: true,
        capture: true
      });

      return () => {
        scrollTarget?.removeEventListener('scroll', handleScroll);
      };
    }, [anchorElement]);

    const setContentXY = useCallback((x: number, y: number) => {
      const { current: contentElement } = contentRef;

      if (!contentElement) {
        return;
      }

      contentElement.style.transform = `translate(${Math.round(
        x
      )}px, ${Math.round(y)}px)`;
    }, []);

    const effectiveDirection = useMemo(() => {
      const { current: wrapperElement } = wrapperRef;

      if (!show || !wrapperElement) {
        return direction;
      }

      const {
        width: anchorWidth,
        height: anchorHeight,
        top: anchorTop,
        left: anchorLeft
      } = anchorRect;

      const { width: contentWidth, height: contentHeight } = contentRect;

      const boundaryElement = findRelativeParent(wrapperElement as Element);

      if (!boundaryElement) {
        return direction;
      }

      const boundaryRect = boundaryElement.getBoundingClientRect();

      // calculate the anchor left x coordinate
      const anchorLeftX = anchorLeft - boundaryRect.left;

      // calculate the anchor center x coordinate
      const anchorCenterX = anchorLeftX + anchorWidth / 2;

      // calculate the Popout x coordinate
      let nextX: number;

      switch (alignment) {
        default:
          throw new Error('Unknown alignment variant.');

        case 'left':
          nextX = anchorLeftX + offsetLeft;
          break;

        case 'right':
          nextX = anchorLeftX + anchorWidth - contentWidth + offsetLeft;
          break;

        case 'center':
          nextX = anchorCenterX - contentWidth / 2 + offsetLeft;
          break;
      }

      // get a bounding width
      const boundingWidth = boundaryRect.width;

      // limit x coordinate if it comes out the right edge
      if (nextX + contentWidth > boundingWidth - MIN_EDGE_DISTANCE) {
        nextX = boundingWidth - contentWidth - offsetLeft;
      }

      // limit x coordinate if it comes out the left edge
      if (nextX < MIN_EDGE_DISTANCE) {
        nextX = offsetLeft;
      }

      // calculate the anchor top y coordinate
      const anchorTopY = anchorTop - boundaryRect.top;

      // calculate y coordinate when Popout is headed downwards
      const yDownward = anchorTopY + anchorHeight + offsetTop;

      // calculate y coordinate when Popout is headed upwards
      const yUpward = anchorTopY - contentHeight - offsetTop;

      // get a bounding height
      const boundingHeight = boundaryRect.height;

      // calculate the popout y coordinate and it's effective direction
      let nextY: number;
      let nextEffectiveDirection: Direction;
      switch (direction) {
        default:
          throw new Error('Unknown direction variant.');

        case 'downward':
          if (
            yDownward + contentHeight > boundingHeight - MIN_EDGE_DISTANCE &&
            yUpward > MIN_EDGE_DISTANCE
          ) {
            nextY = yUpward;
            nextEffectiveDirection = 'upward';
          } else {
            nextY = yDownward;
            nextEffectiveDirection = 'downward';
          }

          break;

        case 'upward':
          if (yUpward < MIN_EDGE_DISTANCE) {
            nextY = yDownward;
            nextEffectiveDirection = 'downward';
          } else {
            nextY = yUpward;
            nextEffectiveDirection = 'upward';
          }

          break;
      }

      // update the content x and y
      setContentXY(nextX, nextY);

      return nextEffectiveDirection;
    }, [
      anchorRect,
      contentRect,
      show,
      offsetTop,
      offsetLeft,
      direction,
      alignment,
      setContentXY
    ]);

    // handle close on click outside
    const outsideClickRef = useOutsideClick((event) => {
      if (!show || !closeOnOutsideClick || !onCloseRequest) {
        return;
      }

      const path = event.composedPath();
      const eventTargetIsAnchorElement =
        anchorElement && path.includes(anchorElement);

      if (!preventCloseOnAnchorElementClick || !eventTargetIsAnchorElement) {
        onCloseRequest(event);

        if (isShadow || eventTargetIsAnchorElement) {
          consumeEvent(event);
        }
      }
    });

    // merge all the content refs
    const contentMergedRef = useMergedRef(
      contentRectRef,
      outsideClickRef,
      contentRef
    );

    // disable the document scroll
    useScrollLock(wrapperRef, disableScroll && show);

    const wrapperMergedRef = useMergedRef(forwardingRef, wrapperRef);

    // handle delayed unmount for animations
    const animationDuration = withoutTransition ? 0 : ANIMATION_DURATION_MS;
    const [isMounted, isClosing] = useDelayedUnmount(show, animationDuration);
    const isVisible = show && !isClosing;

    // init inner properties
    const innerProperties: PopoutContentInnerProperties = {
      effectiveDirection,
      anchorWidth: anchorRect.width,
      anchorHeight: anchorRect.height
    };

    const FinalBackdrop = backdropComponent ? (
      cloneElement(backdropComponent, {
        ...backdropComponent.props,
        style: {
          zIndex,
          ...backdropComponent.props.style
        }
      })
    ) : (
      <Backdrop
        background={backdropColor}
        zIndex={zIndex}
        open={show}
        withoutTransition={withoutTransition}
        fixed
      />
    );

    return (
      isMounted && (
        <Portal containerElement={portalContainerElement} shadow={shadow}>
          <PopoutWrapper
            ref={wrapperMergedRef}
            className={createReadableClass({
              component: 'popout',
              element: 'wrapper'
            })}
          >
            {withBackdrop && FinalBackdrop}

            <PopoutContent
              _animationDuration={animationDuration}
              ref={contentMergedRef}
              className={createReadableClass(
                {
                  component: 'popout',
                  element: 'content'
                },
                [isVisible ? '' : CLOSING_CLASS]
              )}
              style={{ zIndex, ...contentStyles }}
            >
              {typeof children === 'function'
                ? children(innerProperties)
                : children}
            </PopoutContent>
          </PopoutWrapper>
        </Portal>
      )
    );
  }
);

const PopoutWrapper = styled.div``;

const PopoutContent = styled.div<{
  _animationDuration: number;
}>`
  ${({ _animationDuration }) => css`
    position: absolute;
    top: 0;
    left: 0;
    transition: opacity ${_animationDuration}ms ease-out;

    &.${CLOSING_CLASS} {
      opacity: 0;
    }

    @starting-style {
      & {
        opacity: 0;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      & {
        transition: none;
      }
    }
  `}
`;
